/**
 * GA4 Watch Filter & Sort Tracking
 * --------------------------------
 * Sends structured events directly to GA4 (property G-9C4BEMWDT9) when shoppers
 * filter or sort watch collections, via gtag('event', ...).
 *
 * Events:
 *   watch_filter_brand        { brand_name }
 *   watch_filter_price        { price_range }
 *   watch_sort_selected       { sort_option, sort_value }
 *   watch_filter_combination  { brand, price_range, model, case_size, papers, ... }
 *
 * Why gtag and not dataLayer: the theme's GTM container (GTM-TSVZF53P) loads the
 * GA4 config for G-9C4BEMWDT9 at runtime, but has no tag forwarding these custom
 * events. Sending them with gtag('event', name, { send_to: GA4_ID, ... })
 * delivers them straight to the GA4 property without requiring a GTM tag.
 *
 * MAINTAINER NOTE: this depends on the GA4 config (G-9C4BEMWDT9) being present on
 * the page at runtime — currently injected by the GTM container. If GA4 is ever
 * migrated out of that container, update the measurement id (set in
 * snippets/ga4-watch-filters.liquid) to the new GA4 tag id, or the send_to
 * target will no longer exist and these events will silently stop reaching GA4.
 * To route through GTM instead, replace the emit() body with a dataLayer.push
 * and build the matching GTM triggers/tags.
 *
 * Strategy: subscribe to the theme's existing `facetUpdate` pub/sub event, which
 * fires after every filter/sort apply (the theme re-renders via the Section
 * Rendering API and updates the URL). We read the resulting state from the URL,
 * diff it against the previous state to detect which control the user just
 * changed, and emit the relevant events. No core theme files are modified.
 *
 * Only loaded on watch collections (see snippets/ga4-watch-filters.liquid).
 */
(function () {
  'use strict';

  var config = (window.GA4WatchFilters && window.GA4WatchFilters.config) || {};
  var GA4_ID = config.measurementId || 'G-9C4BEMWDT9';

  // Map raw Shopify filter param_name -> canonical GA4 parameter key.
  // Anything not listed falls back to a slug derived from its param_name.
  var PARAM_TO_KEY = {
    'filter.p.m.filter.brand': 'brand',
    'filter.p.m.filter.model': 'model',
    'filter.p.m.filter.case_size': 'case_size',
    'filter.p.m.filter.extras': 'papers',
    'filter.p.m.filter.metal_type': 'metal_type',
    'filter.p.m.filter.dial_color': 'dial_color'
  };

  // Sort value -> readable label fallback (used if we can't read the DOM label).
  var SORT_LABELS = {
    'manual': 'Featured',
    'most-relevant': 'Most relevant',
    'best-selling': 'Best selling',
    'title-ascending': 'Alphabetically, A-Z',
    'title-descending': 'Alphabetically, Z-A',
    'price-ascending': 'Price, low to high',
    'price-descending': 'Price, high to low',
    'created-ascending': 'Date, old to new',
    'created-descending': 'Date, new to old'
  };

  // Emit an event to GA4. send_to targets the specific property so delivery does
  // not depend on a GTM tag existing for these events.
  function emit(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    var payload = {};
    for (var k in params) {
      if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
    }
    payload.send_to = GA4_ID;
    window.gtag('event', eventName, payload);
  }

  function keyForParam(param) {
    if (PARAM_TO_KEY[param]) return PARAM_TO_KEY[param];
    var parts = param.split('.');
    return parts[parts.length - 1] || param;
  }

  function formatMoney(amount) {
    var currency = config.currency || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return currency + ' ' + amount;
    }
  }

  function priceRangeLabel(gte, lte) {
    if (gte && lte) return formatMoney(gte) + '–' + formatMoney(lte); // en dash
    if (gte) return formatMoney(gte) + '+';
    if (lte) return 'Up to ' + formatMoney(lte);
    return '';
  }

  /**
   * Read the current filter/sort selection from a URLSearchParams instance.
   */
  function readState(params) {
    var filters = {};
    var priceGte = null;
    var priceLte = null;

    params.forEach(function (value, name) {
      if (name === 'sort_by' || name === 'page' || name === 'section_id') return;

      if (name === 'filter.v.price.gte') {
        priceGte = value;
        return;
      }
      if (name === 'filter.v.price.lte') {
        priceLte = value;
        return;
      }
      if (name.indexOf('filter.') === 0) {
        var key = keyForParam(name);
        if (!filters[key]) filters[key] = [];
        if (value !== '' && filters[key].indexOf(value) === -1) {
          filters[key].push(value);
        }
      }
    });

    var priceRange = (priceGte || priceLte) ? priceRangeLabel(priceGte, priceLte) : null;

    // Sort: the URL omits sort_by when the collection's default sort is active,
    // so fall back to the currently checked sort radio. This keeps the initial
    // state in sync with what's actually applied and prevents the default sort
    // from being misread as a user-initiated sort change.
    var sort = params.get('sort_by');
    if (!sort) {
      var checked = document.querySelector('input[name="sort_by"]:checked');
      sort = checked ? checked.value : null;
    }

    return {
      filters: filters,
      priceRange: priceRange,
      sort: sort || null
    };
  }

  function currentState() {
    return readState(new URLSearchParams(window.location.search));
  }

  function addedValues(prevArr, nextArr) {
    prevArr = prevArr || [];
    nextArr = nextArr || [];
    return nextArr.filter(function (v) {
      return prevArr.indexOf(v) === -1;
    });
  }

  // Build the combination params from a state's active filters.
  // Returns null when no filters are active.
  function buildCombination(state) {
    var hasAny = false;
    var params = {};

    Object.keys(state.filters).forEach(function (key) {
      var vals = state.filters[key];
      if (vals && vals.length) {
        hasAny = true;
        // Single value -> string; multiple -> comma-joined string.
        // (GA4 event params must be scalars, not arrays.)
        params[key] = vals.length === 1 ? vals[0] : vals.join(', ');
      }
    });

    if (state.priceRange) {
      hasAny = true;
      params.price_range = state.priceRange;
    }

    if (!hasAny) return null;

    params.collection = config.collectionHandle || null;
    return params;
  }

  // Resolve a readable sort label.
  function sortLabelFor(value) {
    if (!value) return null;
    var input = document.querySelector('input[name="sort_by"][value="' + cssEscape(value) + '"]');
    if (input) {
      if (input.getAttribute('aria-label')) return input.getAttribute('aria-label').trim();
      var label = input.closest('label') ||
        (input.id ? document.querySelector('label[for="' + cssEscape(input.id) + '"]') : null) ||
        input.nextElementSibling;
      if (label && label.innerText) return label.innerText.trim();
    }
    return SORT_LABELS[value] || value;
  }

  function cssEscape(str) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(str);
    return String(str).replace(/["\\\]]/g, '\\$&');
  }

  // ---- State tracking & emission ---------------------------------------

  var prevState = currentState();

  function handleUpdate() {
    var next = currentState();

    // 1. Brand: emit one event per newly selected brand value.
    var brandAdded = addedValues(prevState.filters.brand, next.filters.brand);
    brandAdded.forEach(function (brand) {
      emit('watch_filter_brand', {
        brand_name: brand,
        collection: config.collectionHandle || null
      });
    });

    // 2. Price: emit when the price range changed and is now set.
    if (next.priceRange && next.priceRange !== prevState.priceRange) {
      emit('watch_filter_price', {
        price_range: next.priceRange,
        collection: config.collectionHandle || null
      });
    }

    // 3. Sort: emit when the sort value changed.
    if (next.sort && next.sort !== prevState.sort) {
      emit('watch_sort_selected', {
        sort_option: sortLabelFor(next.sort),
        sort_value: next.sort,
        collection: config.collectionHandle || null
      });
    }

    // 4. Combination: emit the full active-filter set whenever filters are
    //    active. Skipped entirely when nothing is selected.
    var combination = buildCombination(next);
    if (combination) {
      emit('watch_filter_combination', combination);
    }

    prevState = next;
  }

  function init() {
    if (!window.theme || !window.theme.pubsub || !window.theme.pubsub.PUB_SUB_EVENTS) {
      return;
    }
    window.theme.pubsub.subscribe(
      window.theme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      handleUpdate
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
